import React, { useState, useEffect } from 'react';

const LoadingScreen = ({ progress = 0, message = 'Loading...', onComplete }) => {
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [isCaught, setIsCaught] = useState(false);
  const [showReaction, setShowReaction] = useState(false);

  useEffect(() => {
    // Smoothly animate progress
    const interval = setInterval(() => {
      setAnimatedProgress(prev => {
        // If caught, animate to 100% to show completion
        const targetProgress = isCaught ? 100 : progress;
        if (prev < targetProgress) {
          const increment = prev < 50 ? 4 : prev < 80 ? 2.5 : 1.5;
          return Math.min(prev + increment, targetProgress);
        }
        return prev;
      });
    }, 30);

    return () => clearInterval(interval);
  }, [progress, isCaught]);

  useEffect(() => {
    if (animatedProgress >= 95 && !isCaught) {
      setIsCaught(true);
      setShowReaction(true);
      
      // Wait 1 second to show the user's reaction, then call onComplete
      const timer = setTimeout(() => {
        if (onComplete) {
          onComplete();
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [animatedProgress, isCaught, onComplete]);

  // Doctor position moves from left (0%) to right (85%)
  const doctorPosition = animatedProgress * 0.85;
  // User is fixed at right (100%)
  const userPosition = 100;
  const distance = userPosition - doctorPosition;

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-blue-50 via-white to-gray-50 flex flex-col items-center justify-center">
      <div className="w-full max-w-lg px-6">
        {/* Animation Container - Adjusted for images */}
        <div className="relative h-20 mb-4">
          {/* User/Patient (Fixed on right, ABOVE progress bar) - Using Image 2 */}
          <div 
            className="absolute right-2 top-0 transition-all duration-300"
            style={{ 
              transform: `scale(${distance < 15 ? 1.1 : 1})`,
            }}
          >
            <div className="relative">
              {/* Exclamation marks when scared - more visible */}
              {distance < 25 && distance > 5 && (
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap z-10">
                  <span className="text-2xl font-extrabold text-red-600 animate-bounce drop-shadow-lg">!!!</span>
                </div>
              )}
              
              {/* Speech bubble when caught - shows for 1 second, more visible */}
              {showReaction && (
                <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-white border-4 border-red-500 rounded-lg px-4 py-2 shadow-2xl animate-bounce z-20">
                  <div className="text-base font-black text-red-700">AHHH!</div>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-500"></div>
                </div>
              )}

              {/* User Image - Image 2 (worried person) */}
              <img 
                src="/user-worried.png" 
                alt="User" 
                className="w-20 h-25 object-contain drop-shadow-md"
                style={{
                  filter: distance < 20 ? 'brightness(1.05) saturate(1.1)' : 'none',
                  transition: 'all 0.3s ease'
                }}
                onError={(e) => {
                  // Fallback if image doesn't exist
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </div>

          {/* Doctor with Syringe (Moves from left to right, ABOVE progress bar) - Using Image 1 */}
          <div 
            className="absolute top-0 transition-all duration-500 ease-out"
            style={{ 
              left: `${doctorPosition}%`,
              transform: `translateX(-50%) scale(${distance < 30 ? 1.05 : 1})`,
              opacity: isCaught ? 0 : 1
            }}
          >
            <div className="relative">
              {/* Doctor Image - Image 1 (doctor with syringe) */}
              <img 
                src="/doctor-syringe.png" 
                alt="Doctor" 
                className="w-14 h-14 object-contain drop-shadow-md"
                style={{
                  transform: distance < 30 ? 'scale(1.05)' : 'scale(1)', // Scale up when close, no flip
                  transition: 'all 0.3s ease'
                }}
                onError={(e) => {
                  // Fallback if image doesn't exist
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </div>

          {/* Success effect when caught */}
          {isCaught && (
            <div className="absolute right-2 top-0 animate-ping">
              <div className="bg-green-400 rounded-full w-3 h-3 opacity-75 flex items-center justify-center">
                <span className="text-white text-xs font-bold">✓</span>
              </div>
            </div>
          )}

          {/* Progress bar at bottom - below characters - Enhanced and more engaging */}
          <div className="absolute bottom-0 left-0 right-0 h-2.5 bg-gray-200 rounded-full shadow-inner overflow-hidden">
            {/* Background glow effect */}
            <div 
              className="absolute inset-0 bg-gradient-to-r from-blue-300 via-blue-400 to-blue-500 rounded-full blur-sm opacity-50"
              style={{ width: `${animatedProgress}%` }}
            ></div>
            
            {/* Main progress fill */}
            <div 
              className="h-full bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 rounded-full relative overflow-hidden transition-all duration-300 ease-out"
              style={{ width: `${animatedProgress}%` }}
            >
              {/* Animated shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-40 animate-shimmer"></div>
              
              {/* Pulsing glow at the end */}
              <div 
                className="absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-blue-600 to-transparent rounded-full animate-pulse"
              ></div>
              
              {/* Sparkle particles */}
              {animatedProgress > 10 && (
                <>
                  <div 
                    className="absolute top-1/2 -translate-y-1/2 w-1 h-1 bg-white rounded-full animate-sparkle"
                    style={{ 
                      left: `${(animatedProgress * 0.3) % 100}%`,
                      animationDelay: '0s'
                    }}
                  ></div>
                  <div 
                    className="absolute top-1/2 -translate-y-1/2 w-1 h-1 bg-blue-200 rounded-full animate-sparkle"
                    style={{ 
                      left: `${(animatedProgress * 0.5) % 100}%`,
                      animationDelay: '0.3s'
                    }}
                  ></div>
                  <div 
                    className="absolute top-1/2 -translate-y-1/2 w-1 h-1 bg-white rounded-full animate-sparkle"
                    style={{ 
                      left: `${(animatedProgress * 0.7) % 100}%`,
                      animationDelay: '0.6s'
                    }}
                  ></div>
                </>
              )}
            </div>
            
            {/* Progress indicator dot at the end */}
            {animatedProgress > 5 && (
              <div 
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-blue-600 rounded-full shadow-lg transition-all duration-300 ease-out"
                style={{ 
                  left: `calc(${animatedProgress}% - 6px)`,
                  boxShadow: '0 0 8px rgba(59, 130, 246, 0.8)'
                }}
              >
                <div className="absolute inset-0 bg-blue-500 rounded-full animate-ping opacity-75"></div>
              </div>
            )}
          </div>
        </div>

        {/* Loading Message */}
        <div className="text-center mb-3">
          <h2 className="text-lg font-bold text-gray-800 mb-1">
            {message}
          </h2>
          <p className="text-xs text-gray-600">
            {animatedProgress < 30 && 'Connecting to DrPay...'}
            {animatedProgress >= 30 && animatedProgress < 60 && 'Checking your subscription...'}
            {animatedProgress >= 60 && animatedProgress < 90 && 'Loading your dashboard...'}
            {animatedProgress >= 90 && !isCaught && 'Almost there...'}
            {isCaught && '✓ Ready to go!'}
          </p>
        </div>

        {/* Progress Percentage */}
        <div className="text-center">
          <div className="inline-block bg-white rounded-full px-4 py-1.5 shadow-md border-2 border-blue-200">
            <span className="text-sm font-bold text-gray-700">
              {Math.round(animatedProgress)}%
            </span>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes armSwing {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(25deg); }
        }
        
        @keyframes armWave {
          0%, 100% { transform: rotate(-45deg) translateY(0px); }
          50% { transform: rotate(-45deg) translateY(-2px); }
        }
        
        @keyframes legRun {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-2px); }
        }
        
        @keyframes syringeThrust {
          0%, 100% { transform: rotate(-60deg) translateX(0px); }
          50% { transform: rotate(-70deg) translateX(-4px); }
        }
        
        @keyframes syringeShake {
          0%, 100% { transform: rotate(15deg); }
          25% { transform: rotate(25deg); }
          75% { transform: rotate(5deg); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes sparkle {
          0%, 100% { 
            opacity: 0;
            transform: translateY(0) scale(0);
          }
          50% { 
            opacity: 1;
            transform: translateY(-4px) scale(1);
          }
        }
        
        .animate-shimmer {
          animation: shimmer 2s ease-in-out infinite;
        }
        
        .animate-sparkle {
          animation: sparkle 1.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;
